import { FundriseRouteConfig } from 'vue-router';
import { validInvestmentPlanSlugs } from '@constants/plans-info';
import { companyIdsToRedirect } from '@constants/reit';
import { useAppStore } from '@stores/app';
import { useInvestmentEntityStore } from '@stores/investment-entity';
import { isAndroidDevice } from '@utils/client';
import { useMixpanel } from '@utils/composables/use-mixpanel';
import { logError } from '@utils/error-tracking';
import { determineUserDashboardRoute } from '@utils/login-redirects';
import { flattenQueryParam } from '@utils/request';
import accountRoutes from './account/account';
import customPlanRoutes from './account/custom-plans';
import investRoutes from './account/invest';
import settingsRoutes from './account/settings';
import accreditationRoutes from './accreditation';
import advisorRoutes from './advisor/advisor';
import appRedirectRoutes from './app-redirect';
import checkoutRoutes from './checkout/checkout';
import customerBetaRoutes from './customer-beta';
import documentRedirectRoutes from './document-redirect';
import educationRoutes from './education';
import emailUnsubscribeRoutes from './email';
import feeWaiverRoutes from './fee-waiver';
import resetPasswordRoutes from './forgot-password/forgot-password';
import fundLandingPageRoutes from './fund-landing-pages';
import growthCalculatorRoutes from './growth-calculator';
import helpCenterRoutes from './help-center';
import investmentGoalRoutes from './investment-goals';
import investmentPlanningRoutes from './investment-planning';
import iraAddFundsRoutes from './ira/ira-add-funds';
import iraCompleteOrderRoutes from './ira/ira-complete-order';
import iraCompleteRolloverRoutes from './ira/ira-complete-rollover';
import iraGeneralRoutes from './ira/ira-general';
import iraReacknowledgeRolloverRoutes from './ira/ira-reacknowledge-rollover';
import iraSignupRoutes from './ira/ira-signup';
import iraVerifyRoutes from './ira/ira-verify';
import legalRoutes from './legal';
import loginRoutes from './login/login';
import marketingRedirectRoutes from './marketing-redirects';
import microFeedbackRoutes from './micro-feedback';
import mobileRedirectRoutes from './mobile-redirect';
import modularLandingPageRoutes from './modular-landing-pages';
import oauthAuthorizationRoutes from './oauth-authorization/oauth-authorization';
import offeringsRoutes from './offerings';
import privateDocumentRedirectRoutes from './private-document-redirect';
import questionInfoFrameworkRoutes from './question-info-framework';
import redemptionRequestRoutes from './redemption-request';
import redirectRoutes from './redirects';
import referralRoutes from './referral';
import sanityPreviewRoutes from './sanity';
import shareholderVoteRoutes from './shareholder-vote';
import signupRoutes from './signup';
import vcxRoutes from './vcx';

export const routes: FundriseRouteConfig[] = [
	{
		path: `/about`,
		name: 'about',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		component: () => import('@views/marketing-site/about-view.vue')
	},
	{
		path: `/download`,
		name: 'download-app',
		redirect: (to) => {
			const { trackMixpanelEvent } = useMixpanel(to);
			const EVENT_NAME = 'App Download Redirect';
			const PROPERTY_REDIRECT_NAME = 'Redirect Destination';

			let url: string;
			if (isAndroidDevice()) {
				url = `${import.meta.env.VITE_AVATAR_URL}/play-store-redirect`;
			} else {
				url = `${import.meta.env.VITE_AVATAR_URL}/app-store-redirect`;
			}

			trackMixpanelEvent(EVENT_NAME, {
				[PROPERTY_REDIRECT_NAME]: url
			});
			window.location.href = url;

			return { name: 'home' };
		}
	},
	{
		path: `/press`,
		name: 'press',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		component: () => import('@views/marketing-site/press-and-media.vue')
	},
	{
		path: `/`,
		name: 'home',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		component: () => import('@views/marketing-site/home-view.vue'),
		beforeEnter: async () => {
			const appStore = useAppStore();
			if (appStore.isAuthenticated) {
				return determineUserDashboardRoute();
			}
		}
	},
	{
		path: `/how-it-works`,
		name: 'how-it-works',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		component: () => import('@views/marketing-site/how-it-works.vue')
	},
	{
		path: `/investments`,
		alias: '/investments-explorer',
		name: 'investments',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		redirect: { name: 'home' }
	},
	{
		path: '/private-credit',
		name: 'private-credit',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		component: () => import('@views/marketing-site/private-credit.vue')
	},
	{
		path: `/venture`,
		name: 'venture-capital',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		component: () => import('@views/marketing-site/venture-capital.vue')
	},
	{
		path: `/investments/advanced-plans/compare`,
		name: 'compare-advanced-plans',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		redirect: { name: 'compare-core-plans' }
	},
	{
		path: `/investments/core-plans/:investmentStrategySlug/view-plan-variants`,
		name: 'start-investing-core-plans-variants-redirect',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		redirect: { name: 'compare-core-plans' }
	},
	{
		path: `/investments/advanced-plans/:planSlug`,
		props: true,
		name: 'plan-detail-pages',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		beforeEnter: (to) => {
			const plan = to.params.planSlug as string;
			if (!validInvestmentPlanSlugs.includes(plan)) {
				return { name: '404' };
			}
		},
		component: () => import('@views/marketing-site/advanced-plan.vue')
	},
	{
		path: `/jobs`,
		name: 'careers',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		component: () => import('@views/marketing-site/careers-view.vue')
	},
	{
		path: `/assets`,
		alias: '/assets/**',
		name: 'assets',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		component: () => import('@views/marketing-site/assets-view.vue')
	},
	{
		path: `/referral-program/landing`,
		name: 'investor-referral-landing-redirect',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		redirect: { name: 'referral-landing' }
	},
	{
		path: `/invitation-program/landing`,
		name: 'referral-landing',
		meta: { minTokenScopeRequired: 'FULL_ACCESS' },
		beforeEnter: async (to, from, next) => {
			const investmentEntityStore = useInvestmentEntityStore();
			if (investmentEntityStore.userReferralEligibilityStatus === 'INELIGIBLE') {
				next({ name: 'account-overview' });
			} else {
				next();
			}
		},
		component: () => import('@views/referral/referral-landing.vue')
	},
	{
		path: `/404`,
		name: '404',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		component: () => import('@views/error/not-found-404.vue')
	},
	{
		path: `/forbidden`,
		name: 'forbidden',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		component: () => import('@views/error/forbidden-view.vue')
	},
	{
		path: `/guides/real-estate-investing-guide`,
		name: 'rei-guide',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		component: () => import('@views/marketing-site/rei-guide.vue')
	},
	{
		path: `/why-fundrise`,
		name: 'why-fundrise',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		component: () => import('@views/marketing-site/why-fundrise.vue')
	},
	{
		path: `/benefits`,
		name: 'premium-landing',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		component: () => import('@views/marketing-site/premium-landing-page.vue')
	},

	{
		path: `/advanced-investing`,
		name: 'high-end-offerings',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		redirect: { name: 'premium-landing' }
	},
	{
		path: `/why-private-real-estate`,
		name: 'why-private-real-estate',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		redirect: { name: 'real-estate-strategies' }
	},
	{
		path: `/real-estate-assets/:reitElementId/view`,
		name: 'real-estate-assets',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED', suppressDefaultMxpViewEvent: true },
		component: () => import('@views/marketing-site/asset-details-wrapper.vue')
	},
	{
		path: `/company-assets/:companyId/view`,
		name: 'company-assets',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED', suppressDefaultMxpViewEvent: true },
		beforeEnter: async (to) => {
			const companyId = flattenQueryParam(to.params.companyId);
			if (companyId && companyIdsToRedirect.includes(companyId)) {
				return { name: 'vcx-landing' };
			}
			const appStore = useAppStore();
			const investmentEntityStore = useInvestmentEntityStore();
			if (!appStore.isAuthenticated || !investmentEntityStore.isReitInvestor) {
				return { name: 'vcx-landing' };
			}
		},
		component: () => import('@views/marketing-site/asset-details-wrapper.vue')
	},
	{
		path: `/bond-assets/:bondId/view`,
		name: 'bond-assets',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED', suppressDefaultMxpViewEvent: true },
		component: () => import('@views/marketing-site/asset-details-wrapper.vue')
	},
	{
		path: `/venture-fund-assets/:ventureFundId/view`,
		name: 'venture-fund-assets',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED', suppressDefaultMxpViewEvent: true },
		component: () => import('@views/marketing-site/asset-details-wrapper.vue')
	},
	{
		path: `/real-estate-strategies`,
		name: 'real-estate-strategies',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		component: () => import('@views/marketing-site/real-estate-strategies.vue')
	},
	{
		path: `/strategy`,
		name: 'strategy',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		redirect: { name: 'real-estate-strategies' }
	},
	{
		path: `/ereits`,
		name: 'ereits',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		redirect: { name: 'real-estate-strategies' }
	},
	{
		path: `/client-returns`,
		name: 'client-returns',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		component: () => import('@views/marketing-site/client-returns.vue')
	},
	{
		path: `/connect-api`,
		name: 'connect-api',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		component: () => import('@views/marketing-site/connect-api.vue')
	},
	{
		path: `/connect-api/contact`,
		name: 'connect-api-contact',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		component: () => import('@views/marketing-site/connect-api-contact.vue')
	},
	{
		path: `/webview/welcome-screen`,
		name: 'webview-welcome-screen',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		component: () => import('@views/webview/webview-welcome-screen.vue')
	},
	{
		path: `/webview/plan-details/:slug`,
		name: 'webview-plan-details',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		component: () => import('@views/webview/plan-details.vue')
	},

	{
		path: `/webview/legal-disclosure`,
		name: 'webview-legal-disclosure',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		component: () => import('@views/webview/legal-disclosure.vue')
	},
	{
		path: `/webview/track-record-assumptions`,
		name: 'webview-track-record-assumptions',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		component: () => import('@views/webview/track-record-assumptions.vue')
	},
	{
		path: '/investor-update/:investorUpdateId/view',
		name: 'investor-update',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		beforeEnter: async (to, from, next) => {
			const isId = /^\d+$/.test(to.params.investorUpdateId as string);
			if (isId) {
				next();
			} else {
				next({ name: '404' });
			}
		},
		component: () => import('@views/investor-update/investor-update.vue')
	},
	{
		path: '/property-update/:investorUpdateId/view',
		name: 'property-update',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		beforeEnter: async (to, from, next) => {
			const isId = /^\d+$/.test(to.params.investorUpdateId as string);
			if (isId) {
				next();
			} else {
				next({ name: '404' });
			}
		},
		component: () => import('@views/investor-update/investor-update.vue')
	},
	{
		path: `/get-iphone-app`,
		name: 'get-iphone-app',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		component: () => import('@views/mobile/get-iphone-app.vue')
	},
	{
		path: `/get-android-app`,
		name: 'get-android-app',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		component: () => import('@views/mobile/get-android-app.vue')
	},
	{
		path: `/contact`,
		name: 'contact',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		component: () => import('@views/contact/contact-view.vue')
	},
	{
		path: '/data-visualizations/:dataVisualization',
		name: 'data-visualization',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		component: () => import('@views/data-visualization/data-visualization.vue')
	},
	{
		path: '/maintenance',
		name: 'maintenance',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		beforeEnter: async (to, from, next) => {
			const appStore = useAppStore();
			const isMaintenanceWindow = await appStore.isFeatureEnabled('app-down-maintenance-window');
			if (!isMaintenanceWindow) {
				next({ name: 'home' });
			} else {
				next();
			}
		},
		component: () => import('@views/maintenance-page.vue')
	},
	{
		path: '/test',
		name: 'test-route',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		redirect(to) {
			if (to.query.testId === 'error') {
				logError('Test Error', 'info');
			}

			// must clear query in redirect
			return { name: 'home', query: {} };
		}
	},
	...accountRoutes,
	...advisorRoutes,
	...appRedirectRoutes,
	...checkoutRoutes,
	...customPlanRoutes,
	...customerBetaRoutes,
	...documentRedirectRoutes,
	...educationRoutes,
	...emailUnsubscribeRoutes,
	...feeWaiverRoutes,
	...fundLandingPageRoutes,
	...growthCalculatorRoutes,
	...helpCenterRoutes,
	...investmentGoalRoutes,
	...investmentPlanningRoutes,
	...investRoutes,
	...iraGeneralRoutes,
	...iraSignupRoutes,
	...legalRoutes,
	...loginRoutes,
	...marketingRedirectRoutes,
	...microFeedbackRoutes,
	...mobileRedirectRoutes,
	...modularLandingPageRoutes,
	...oauthAuthorizationRoutes,
	...offeringsRoutes,
	...sanityPreviewRoutes,
	...privateDocumentRedirectRoutes,
	...questionInfoFrameworkRoutes,
	...redemptionRequestRoutes,
	...redirectRoutes,
	...referralRoutes,
	...resetPasswordRoutes,
	...settingsRoutes,
	...shareholderVoteRoutes,
	...signupRoutes,
	...vcxRoutes,
	accreditationRoutes,
	iraAddFundsRoutes,
	iraCompleteOrderRoutes,
	iraCompleteRolloverRoutes,
	iraReacknowledgeRolloverRoutes,
	iraVerifyRoutes
];
